import React, { useState } from 'react';
import { seriesSnapshotsApi } from '../../api/seriesSnapshots';
import { emptySnapshots, newCardId, upsertCard } from '../../utils/seriesSnapshots';
import { SAVE_TO_SERIES_NOTES } from '../../utils/writerCopy';
import './AddToBibleModal.css';

/**
 * SaveToSeriesNotesModal — "Save to series notes" (Joel lock #1417).
 *
 * The one bridge from a series chat to Series Notes. Copies the message word for word
 * into a new Custom series note; the writer gives it a title. Nothing is inferred,
 * split or merged, and nothing is saved until the writer presses Save.
 *
 * Props:
 * - projectId, messageContent
 * - onClose(): close without saving
 * - onSaved(title): called after a successful save
 */
export function defaultSeriesNoteTitle(text) {
  const words = String(text || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const head = words.slice(0, 6).join(' ');
  return words.length > 6 ? `${head}…` : head;
}

const SaveToSeriesNotesModal = ({ projectId, messageContent, onClose, onSaved }) => {
  const [title, setTitle] = useState(() => defaultSeriesNoteTitle(messageContent));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const name = title.trim();
    if (!name) return;
    setError('');
    setIsSaving(true);
    try {
      const current = await seriesSnapshotsApi.get(projectId);
      const store = current?.cards ? current : emptySnapshots();
      const next = upsertCard(store, {
        id: newCardId(),
        kind: 'custom',
        name,
        description: messageContent || '',
        writeup: '',
      });
      await seriesSnapshotsApi.put(projectId, next);
      if (onSaved) onSaved(name);
      onClose();
    } catch (err) {
      console.error('Failed to save to series notes:', err);
      setError('Could not save this. Please try again.');
      setIsSaving(false);
    }
  };

  return (
    <div className="add-to-bible-backdrop" onClick={onClose}>
      <div
        className="add-to-bible-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={SAVE_TO_SERIES_NOTES}
      >
        <div className="add-to-bible-header">
          <div>
            <h3>{SAVE_TO_SERIES_NOTES}</h3>
            <p className="add-to-bible-subtitle">
              This message is saved word for word as a new note in Series Notes. Give it a title.
            </p>
          </div>
          <button
            className="add-to-bible-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        <div className="add-to-bible-content">
          <label className="add-to-bible-card-name" htmlFor="series-note-title">Title</label>
          <input
            id="series-note-title"
            className="add-to-bible-card-name"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isSaving}
          />
          <p className="add-to-bible-card-desc">{messageContent}</p>
          {error && <span className="add-to-bible-error">{error}</span>}
        </div>

        <div className="add-to-bible-footer">
          <button
            type="button"
            className="add-to-bible-cancel-btn"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="add-to-bible-submit-btn"
            onClick={handleSave}
            disabled={isSaving || !title.trim()}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveToSeriesNotesModal;
